import Chart, { useChart } from 'src/components/chart';
import { useMemo, useRef } from 'react';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { Box, useTheme } from '@mui/system';

type Props = {
    data: any[];
    rateMode: 'blended' | 'winrate' | 'paid' | string;
    mainDomain?: string;
    isAbsMode: boolean;
    activeDomains?: string[];
    getColor?: (domain: string) => string;
};

export default function ChartEvolution({ data, rateMode, mainDomain, isAbsMode, activeDomains, getColor }: Props) {
    const rawData = Array.isArray(data) ? data : [];
    const safeData =
        activeDomains && activeDomains.length > 0
            ? rawData.filter((d: any) => activeDomains.includes(d.domain_name))
            : rawData;
    const hoveredSeriesRef = useRef<number | null>(null); // ⚡ ref pour stocker l'index
    const chartContainerRef = useRef<HTMLDivElement>(null); // ref pour scoper les sélecteurs
    const theme = useTheme();

    // ✅ Sélection de la bonne métrique selon mode
    const metricKey = useMemo(() => {
        if (rateMode === 'blended') return isAbsMode ? 'abs_top_blended_presence_rate' : 'top_blended_presence_rate';
        if (rateMode === 'winrate') return isAbsMode ? 'abs_top_competitor_win_rate' : 'top_competitor_win_rate';
        return isAbsMode ? 'abs_top_paid_presence_rate' : 'top_paid_presence_rate';
    }, [rateMode, isAbsMode]);

    // ✅ Liste des domaines uniques
    const domains = useMemo(
        () => Array.from(new Set(safeData.map((d: any) => d.domain_name))),
        [safeData]
    );

    // ✅ Dates : supporte date_day et date_group
    const categories = useMemo(
        () => Array.from(new Set(safeData.map((d: any) => d.date_group || d.date_day))).sort(),
        [safeData]
    );

    // ✅ Construction des séries
    const series: any[] = useMemo(
        () =>
            domains.map((domain) => ({
                name: domain,
                data: categories.map((date) => {
                    const point = safeData.find(
                        (d: any) => d.domain_name === domain && (d.date_group || d.date_day) === date
                    );
                    return { x: date, y: point ? point[metricKey] : null };
                }),
            })),
        [domains, categories, safeData, metricKey]
    );

    // 🎯 Palette
    const palette = useMemo(() => {
        if (getColor) return series.map((s) => getColor(s.name));

        const OTHER = ['#DFD09D', '#11B5FF', '#7CD3AD', '#DDBFE9'];
        const MAIN = '#e8ddff';

        return series.map((s, i) => {
            if (s.name === mainDomain) return MAIN;

            const offset = i > series.findIndex(x => x.name === mainDomain) ? i - 1 : i;

            return OTHER[offset % OTHER.length];
        });
    }, [series, mainDomain, getColor]);

    // ✅ Titre axe Y
    const yTitle = useMemo(() => {
        if (rateMode === 'blended') return 'Visibility Rate';
        if (rateMode === 'winrate') return 'Competitor Win Rate';
        return 'Paid Presence Rate';
    }, [rateMode]);

    // ✅ Max Y dynamique
    const maxY = useMemo(() => {
        let max = 0;
        for (const s of series) {
            for (const pt of s.data) {
                if (pt.y !== null && pt.y !== undefined) max = Math.max(max, pt.y);
            }
        }
        return Math.ceil(max / 10) * 10;
    }, [series]);

    const workspace = useCurrentWorkspace();

    const chartOptions = useChart({
        chart: {
            type: 'line',
            toolbar: { show: false },
            zoom: { enabled: false },
            animations: { enabled: true },
            events: {
                dataPointMouseEnter(config) {
                    hoveredSeriesRef.current = config.seriesIndex; // ⚡ update ref
                },
                dataPointMouseLeave() {
                    hoveredSeriesRef.current = null; // reset ref
                },
                mouseMove: (event: any, chartContext: any, config: any) => {
                    const { seriesIndex } = config;
                    const container = chartContainerRef.current;
                    if (!container) return;

                    // reset styles (scoped to this chart only)
                    container.querySelectorAll('.apexcharts-series').forEach(el => {
                        (el as HTMLElement).style.opacity = '0.3';
                        (el as HTMLElement).style.strokeWidth = '2px';
                    });

                    // highlight only hovered serie
                    if (seriesIndex >= 0) {
                        const el = container.querySelector(`.apexcharts-series[data\\:realIndex="${seriesIndex}"]`) as HTMLElement;
                        if (el) {
                            el.style.opacity = '1';
                            el.style.strokeWidth = '4px';
                        }
                    }
                },
                mouseLeave: () => {
                    const container = chartContainerRef.current;
                    if (!container) return;

                    // reset everything (scoped to this chart only)
                    container.querySelectorAll('.apexcharts-series').forEach(el => {
                        (el as HTMLElement).style.opacity = '1';
                        (el as HTMLElement).style.strokeWidth = '2px';
                    });
                },
            }
        },
        colors: palette,
        stroke: { curve: 'smooth', width: 2 },
        markers: {
            size: 0,
            colors: palette,
            strokeColors: palette,
        },

        tooltip: {
            shared: true,
            intersect: false,
            x: { show: false },
            custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                const date = w.globals.categoryLabels[dataPointIndex];

                // ⚡ Nom exact de la série actuellement survolée
                const activeSeriesName = w.globals.hoverSeries?.name || w.globals.seriesNames[seriesIndex];

                const items = w.config.series.map((s: any, i: number) => ({
                    name: s.name,
                    value: series[i][dataPointIndex],
                    isHovered: s.name === activeSeriesName,
                    color: w.globals.colors[i],
                }));

                // 👉 Tri décroissant
                items.sort((a: any, b: any) => (b.value ?? 0) - (a.value ?? 0));

                const rows = items.map((item: any) => `
                    <div style="display:flex; justify-content:space-between; align-items:center; margin:4px 0; width:100%;
                                ">
                        <div style="display:flex; align-items:center; gap:8px;">
                        <span style="display:inline-block; width:10px; height:10px; border-radius:2px; background:${item.color}; flex-shrink:0;"></span>
                        <span style="display:inline-flex; align-items:center; justify-content:center; width:20px; height:20px; background:white; border-radius:50%;">
                            <img src="https://www.google.com/s2/favicons?domain=${item.name}&sz=32"
                                style="width:16px; height:16px; border-radius:3px;" />
                            </span>

                        <span style="color: ${theme.palette.mode === 'dark' ? "#FFF" : "black"}; font-size:13px; font-weight:${item.isHovered ? "900" : "500"};">
                            ${item.name}
                        </span>
                        </div>
                        <span style="color: ${theme.palette.mode === 'dark' ? "#FFF" : "black"}; font-size:13px; font-weight:${item.isHovered ? "900" : "500"};">
                        ${typeof item.value === "number" ? item.value.toFixed(0) + "%" : "-"}
                        </span>
                    </div>
                    `).join("");

                return `
                    <div style="border-radius:12px; border: ${theme.palette.mode === 'dark' ? "1px solid #2A323B" : "1px solid white"}; background: ${theme.palette.mode === 'dark' ? "rgba(33,43,54,0.8)" : "rgba(255,255,255,0.8)"}; padding:12px 16px; min-width:220px;">
                        <div style="color:${theme.palette.mode === 'dark' ? "#FFF" : "black"}; font-size:14px; font-weight:700; margin-bottom:6px;">${date}</div>
                        ${rows}
                    </div>
                    `;
            }
        },

        legend: { show: false },
        dataLabels: {
            enabled: true,
            textAnchor: 'middle',

            formatter: (_val: number, { seriesIndex, dataPointIndex, w }) => {
                const seriesData = w.config.series[seriesIndex].data;
                const isLast = dataPointIndex === seriesData.length - 1;
                if (isLast && w.config.series[seriesIndex].name === workspace?.url) return w.config.series[seriesIndex].name;
                return '';
            },
            background: {
                enabled: true,
                borderRadius: 3,
                padding: 12,
                opacity: 0.9,
                borderColor: 'rgba(255, 255, 255, 0.20)',
                foreColor: "#ffffff",
            },
            style: {
                fontSize: '12px',
            },
            offsetY: -10,
        },
        xaxis: {
            categories,
            labels: { style: { colors: '#aaa', fontSize: '12px' }, rotate: -45 },
            tooltip: { enabled: false },
        },
        yaxis: {
            min: 0,
            max: maxY || 100,
            tickAmount: 8,
            labels: { formatter: (val: number) => `${val?.toFixed(0)}%`, style: { colors: '#aaa', fontSize: '12px' } },
            title: {
                text: yTitle,
                style: {
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#aaa'
                }
            },
        },
        grid: {
            borderColor: 'rgba(255,255,255,0.2)',
            strokeDashArray: 4,
            xaxis: { lines: { show: true } },
            yaxis: { lines: { show: false } },
        },
    });

    return (
        <Box ref={chartContainerRef} sx={{ minHeight: 0, width: '100%', height: '100%', px: 2 }}>
            <Chart
                options={chartOptions}
                series={series}
                type="line"
                width="100%"
                height='100%'
            />
        </Box>
    );

}
